import { useState } from "react";
import { Bot, User, Cog, ChevronDown, ChevronRight } from "lucide-react";
import { useDisponibilidadeSinais, type SinalDisponibilidade } from "@/hooks/useDisponibilidade";
import { EIXO_LABEL, axisValueLabel, fonteLabel } from "@/utils/disponibilidadeUtils";
import { formatDateOnly } from "@/utils/dateUtils";
import { DisponibilidadeTimeline } from "./DisponibilidadeTimeline";

const AUTHOR_ICON = { humano: User, ia: Bot, sistema: Cog };

function EventRow({ sinal }: { sinal: SinalDisponibilidade }) {
  const [conversaAberta, setConversaAberta] = useState(false);
  const AuthorIcon = AUTHOR_ICON[sinal.author_kind as keyof typeof AUTHOR_ICON] ?? Cog;
  const ChevronIcon = conversaAberta ? ChevronDown : ChevronRight;

  return (
    <li className="flex gap-3 py-2 border-b last:border-b-0">
      <div className="w-24 shrink-0 text-xs text-muted-foreground tabular-nums">
        {formatDateOnly(sinal.observed_at)}
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-sm">
          <span className="text-muted-foreground">{EIXO_LABEL[sinal.dimension] ?? sinal.dimension}:</span>{" "}
          <span className="font-medium">{axisValueLabel(sinal.dimension, sinal.value)}</span>
        </div>
        <div className="flex items-center gap-1.5 text-xs text-muted-foreground mt-0.5">
          <AuthorIcon className="h-3 w-3" />
          {fonteLabel(sinal.source_type) || sinal.source_type}
          <span>· confiança {sinal.confidence}</span>
          {sinal.review_status === "pendente_curadoria" && (
            <span className="text-amber-600">· a confirmar</span>
          )}
        </div>
        {sinal.observation && (
          <p className="text-xs text-muted-foreground mt-1 whitespace-pre-wrap wrap-break-word">
            {sinal.author_kind === "ia" && (
              <span className="font-medium text-foreground/70">Resumo: </span>
            )}
            {sinal.observation}
          </p>
        )}
        {sinal.quote && (
          <div className="mt-1">
            <button
              type="button"
              onClick={() => setConversaAberta((aberta) => !aberta)}
              className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
            >
              <ChevronIcon className="h-3 w-3" />
              {conversaAberta ? "Esconder conversa" : "Ver conversa"}
            </button>
            {conversaAberta && (
              <pre className="mt-1 max-h-64 overflow-y-auto rounded border bg-muted/40 p-2 text-xs font-sans whitespace-pre-wrap wrap-break-word text-muted-foreground">
                {sinal.quote}
              </pre>
            )}
          </div>
        )}
      </div>
    </li>
  );
}

/**
 * Histórico de disponibilidade (timeline + eventos) de um freelancer. Usado tanto no
 * dialog dedicado quanto na aba "Histórico" do dialog de atualizar.
 * `enabled=false` não busca nada (aba fechada).
 */
export function HistoricoDisponibilidadeContent({
  freelancerId,
  enabled = true,
  monthsBack = 36,
}: {
  freelancerId: string;
  enabled?: boolean;
  monthsBack?: number;
}) {
  const { data: sinais, isLoading } = useDisponibilidadeSinais(enabled ? freelancerId : null);

  return (
    <div className="space-y-6">
      <DisponibilidadeTimeline
        freelancerId={enabled ? freelancerId : null}
        monthsBack={monthsBack}
      />

      <div>
        <h3 className="text-sm font-medium text-muted-foreground mb-1">
          Eventos que mudaram a situação
        </h3>
        {isLoading ? (
          <p className="text-xs text-muted-foreground">Carregando eventos...</p>
        ) : !sinais?.length ? (
          <p className="text-xs text-muted-foreground">
            Nenhum evento registrado ainda. Atualize a disponibilidade para começar.
          </p>
        ) : (
          <ul>
            {sinais.map((sinal) => (
              <EventRow key={sinal.id} sinal={sinal} />
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

export default HistoricoDisponibilidadeContent;
